import { ComponentType } from "react";
import { Column } from "@tanstack/react-table";
import { ListFilter } from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@pushpress/pushpress-ui";

interface CustomColumnMeta<TData> {
  getChurnDateFilterFacets?: (rows: TData[]) => Map<string, number>;
}

interface MultiDropdownFilterProps<TData, TValue> {
  filters: {
    column: Column<TData, TValue>;
    label: string;
    options: {
      label: string;
      value: string;
      icon?: ComponentType<{ className?: string }>;
    }[];
  }[];
}

export function MultiDropdownFilter<TData, TValue>({
  filters,
}: MultiDropdownFilterProps<TData, TValue>) {
  const filterStates = filters.map((filter) => ({
    facets:
      filter.label === "Churn date"
        ? (
            filter.column.columnDef.meta as CustomColumnMeta<TData>
          )?.getChurnDateFilterFacets(
            filter.column.getFacetedRowModel().rows.map((row) => row.original),
          ) || new Map()
        : filter.column?.getFacetedUniqueValues(),
    selectedValues: new Set(filter.column?.getFilterValue() as string[]),
  }));

  const totalSelectedCount = filterStates.reduce(
    (sum, state) => sum + state.selectedValues.size,
    0,
  );

  const handleChurnDateFilterChange = (
    isSelected: boolean,
    selectedChurnDateFilterValues: Set<string>,
    memberFilterSelectedValues: Set<string>,
    selectedValue: string,
    memberColumn: Column<TData, TValue>,
  ) => {
    if (isSelected) {
      selectedChurnDateFilterValues.delete(selectedValue);
    } else {
      if (!memberFilterSelectedValues.has("ex-member")) {
        memberFilterSelectedValues.add("ex-member");
        memberColumn.setFilterValue(Array.from(memberFilterSelectedValues));
      }
      selectedChurnDateFilterValues.clear();
      selectedChurnDateFilterValues.add(selectedValue);
    }
  };

  const handleRemoveExMemberFilterOption = (
    memberFilterSelectedValues: Set<string>,
    churnDateColumn: Column<TData, TValue>,
    selectedValue: string,
  ) => {
    memberFilterSelectedValues.delete(selectedValue);
    churnDateColumn.setFilterValue(undefined);
  };

  const handleFilterChange = (
    isSelected,
    selectedValue,
    selectedValues,
    churnDateColumn,
  ) => {
    if (!isSelected) {
      selectedValues.add(selectedValue);
    } else if (isSelected && selectedValue === "ex-member") {
      handleRemoveExMemberFilterOption(
        selectedValues,
        churnDateColumn,
        selectedValue,
      );
    } else {
      selectedValues.delete(selectedValue);
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          data-testid="multi-dropdown-filter-button"
          className="!tw-h-8 !tw-shadow-none"
        >
          <div className="tw-flex tw-items-center tw-justify-center">
            {totalSelectedCount > 0 && (
              <Badge
                variant="secondary"
                className="tw-rounded-full tw-px-1 tw-text-xs tw-mr-2"
              >
                {totalSelectedCount}
              </Badge>
            )}
            <ListFilter className="tw-h-5 tw-w-5" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="tw-w-52 max-sm:tw-max-h-[300px] tw-overflow-y-auto tw-max-h-[calc(80vh-120px)]"
      >
        {filters?.map((filter, filterIndex) => (
          <div key={filter.label}>
            {filterIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="tw-text-sm tw-text-start tw-text-xs tw-text-neutral-600">
              {filter.label}
            </DropdownMenuLabel>
            {filter?.options?.map((option) => {
              const isSelected = filterStates[filterIndex]?.selectedValues.has(
                option.value,
              );

              return (
                <DropdownMenuItem
                  key={option.value}
                  className="tw-gap-2 tw-text-xs !tw-py-1"
                  onSelect={(e) => e.preventDefault()}
                  onClick={() => {
                    const selectedValues =
                      filterStates[filterIndex]?.selectedValues;

                    if (filter.label === "Churn date") {
                      handleChurnDateFilterChange(
                        isSelected,
                        selectedValues,
                        filterStates[0]?.selectedValues,
                        option.value,
                        filters[0].column,
                      );
                    } else {
                      handleFilterChange(
                        isSelected,
                        option.value,
                        selectedValues,
                        filters[2].column,
                      );
                    }

                    const filterValues = Array.from(selectedValues);
                    filter.column?.setFilterValue(
                      filterValues.length ? filterValues : undefined,
                    );
                  }}
                >
                  <div className="tw-flex tw-items-center">
                    <Checkbox checked={isSelected} />
                  </div>
                  <span className="tw-text-sm tw-text-neutral-800">
                    {option.label}
                  </span>
                  {filterStates[filterIndex].facets?.get(option.value) > 0 && (
                    <span className="tw-ml-auto tw-flex tw-h-5 tw-w-8 tw-items-center tw-justify-center tw-font-mono tw-text-xs tw-rounded-full tw-bg-neutral-200 tw-text-neutral-800">
                      {filterStates[filterIndex].facets.get(option.value)}
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
        {totalSelectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="tw-justify-center"
              onSelect={(e) => e.preventDefault()}
              onClick={() => {
                filters.forEach((filter) =>
                  filter.column?.setFilterValue(undefined),
                );
              }}
            >
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
